import React, { useState, useRef, useEffect, useCallback } from "react";
import { getIcon, getProjectImage } from "../utils/fun";
import Bubble from "./Bubble";
import LinkBubble from "./LinkBubble";

function ProjectImages({ images, imgIndex, hasLink, projectLink, repoLink, name }) {
    const imgSrc = getProjectImage({ images }, imgIndex);
    const imgAlt = `${name} project screenshot`;
    const img = (
        <img
            src={imgSrc}
            className="card-img-top object-fit-cover w-100 h-100"
            alt={imgAlt}
        />
    );
    if (hasLink) {
        return (
            <a href={projectLink || repoLink} target="_blank" rel="noopener noreferrer">
                {img}
            </a>
        );
    }
    return img;
}

function ProjectContent({ pageIndex, name, description, techStack }) {
    if (pageIndex === 0) {
        return <h3 className="fw-semibold fs-6 m-0">{name}</h3>;
    }
    if (pageIndex === 1 && description) {
        return <span>{description}</span>;
    }
    return (
        <div className="d-flex align-items-center gap-1 p-0">
            <div>Built with:</div>
            {(techStack || []).map((tech, idx) => (
                <div
                 className="d-flex align-items-center" 
                 style={{ height: "1.35em", paddingInline: "3px" }} 
                 key={idx}
                >
                    <img
                        src={getIcon(tech)}
                        alt={tech}
                        title={tech}
                        style={{ height: "100%", objectFit: "contain" }}
                    />
                </div>
            ))}
        </div>
    );
}

function getPageCount({ name, description, techStack }) {
    return (name ? 1 : 0) + (description ? 1 : 0) + (techStack && techStack.length > 0 ? 1 : 0);
}

export default function ProjectCard({
    project: { 
        name = "", 
        description = "", 
        techStack = [], 
        images = [], 
        repoLink = "", 
        projectLink = ""
    },
}) {
    const [pageIndex, setPageIndex] = useState(0);
    const [imgIndex, setImgIndex] = useState(0);
    const intervalRef = useRef(null);

    const hasLink = repoLink || projectLink;
    const pageCount = getPageCount({ name, description, techStack });
    const imgCount = (images && images.length) || 0;

    const startSlideshow = useCallback(() => {
        if (imgCount <= 1 || intervalRef.current) return;
        intervalRef.current = setInterval(() => {
            setImgIndex(i => (i + 1) % imgCount);
            setPageIndex(p => (p + 1) % pageCount);
        }, 1200);
    }, [imgCount, pageCount]);

    const stopSlideshow = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setImgIndex(0);
        setPageIndex(0);
    }, []);

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    const nextPage = () => {
        setPageIndex(p => (p + 1) % pageCount);
        if (imgCount > 0) {
            setImgIndex(i => (i + 1) % imgCount);
        }
    };

    return (
        <div
            className="card d-flex p-0 border-0 rounded-4 primary fs-xs-base"
            style={{
                width: '420px',
                overflow: 'hidden',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            }}
        >
            <div className="d-flex flex-column h-100 w-100">
                <div
                    style={{ aspectRatio: '16/9', overflow: 'hidden' }}
                    onMouseEnter={startSlideshow}
                    onMouseLeave={stopSlideshow}
                    onFocus={startSlideshow}
                    onBlur={stopSlideshow}
                    tabIndex={-1}
                >
                    <ProjectImages
                        images={images}
                        imgIndex={imgIndex}
                        hasLink={hasLink}
                        projectLink={projectLink}
                        repoLink={repoLink}
                        name={name}
                    />
                </div>
                <span className="d-flex p-3 align-items-center gap-2">
                    <ProjectContent
                        pageIndex={pageIndex}
                        name={name}
                        description={description}
                        techStack={techStack}
                    />
                    <span className="ms-auto d-flex gap-3">
                        {repoLink && (
                            <LinkBubble 
                                href={repoLink} 
                                size="1.7rem" 
                                ariaLabel="GitHub" 
                                spawnOnVisible={false}
                            >
                                <i className="bi bi-github primary"></i>
                            </LinkBubble>
                        )}
                        <Bubble
                            size="1.7rem"
                            onClick={nextPage}
                            tabIndex={0}
                            spawnOnVisible={false}
                        >
                            <i className="bi bi-play-fill primary"></i>
                        </Bubble>
                    </span>
                </span>
            </div>
        </div>
    );
}